import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button } from '@robocorp/components';
import { styled } from '@robocorp/theme';
import { IconMenu } from '@robocorp/icons/iconic';
import { useTranslation } from 'react-i18next';

import Breadcrumbs from '~/components/Breadcrumbs';
import WorkspaceKeyActionsDialog from '~/routes/workspace/settings/components/WorkspaceKeyActionsDialog';
import { useDisclosure } from '~/hooks';
import { WorkspaceInfo } from './WorkspaceInfo';
import { HelpMenu } from './HelpMenu';
import { UserMenu } from './UserMenu';
import { CurrentWorkspaceRestriction } from '../permission';
import { ApiHelper } from '../ApiHelper';

const StyledAppBar = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 0px auto auto;
  margin: 0 5rem;
  grid-template-rows: 6rem;
  grid-gap: ${({ theme }) => theme.space.$24};
  grid-template-areas: 'breadcrumbs burger-menu help-menu user-menu';
  background: ${({ theme }) => theme.colors.background.primary.color};

  > nav {
    grid-area: breadcrumbs;
    display: flex;
    align-items: center;
    min-width: 0;
  }

  > div.help-menu {
    grid-area: help-menu;
  }

  > div.user-menu {
    grid-area: user-menu;
  }

  > div.burger-menu {
    display: none;
    grid-area: burger-menu;

    ${({ theme }) => theme.screen.m} {
      display: inline-flex;
    }
  }

  ${({ theme }) => theme.screen.m} {
    margin: 0px;
    padding: ${({ theme }) => theme.space.$16} ${({ theme }) => theme.space.$32}
      ${({ theme }) => theme.space.$16};
    grid-template-areas: 'user-menu help-menu burger-menu' 'breadcrumbs breadcrumbs breadcrumbs';
    grid-template-columns: ${({ theme }) => theme.sizes.$32} auto ${({ theme }) => theme.sizes.$40};
    grid-template-rows: ${({ theme }) => theme.sizes.$40};
    grid-gap: ${({ theme }) => theme.space.$16};
  }

  ${({ theme }) => theme.screen.s} {
    padding: ${({ theme }) => theme.space.$16} ${({ theme }) => theme.space.$16} 0;
  }
`;

interface TopNavigationProps {
  onToggle: () => void;
}

export const TopNavigation: FC<TopNavigationProps> = ({ onToggle }) => {
  const { t } = useTranslation();
  const [showHelper, showHelperHandler] = useDisclosure();

  const { workspaceShortName } = useParams<'workspaceShortName'>();
  const isWorkspaceLevelRoute = workspaceShortName !== undefined;

  return (
    <>
      <StyledAppBar id="application__bar" as="header">
        <Breadcrumbs />

        <Box
          className="help-menu"
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap="$24"
        >
          {isWorkspaceLevelRoute && <WorkspaceInfo onOpen={showHelperHandler.toggle} />}
          <HelpMenu />
        </Box>

        <Box className="user-menu" display="flex" alignItems="center" gap="$24">
          <UserMenu />
        </Box>

        <Box className="burger-menu">
          <Button
            aria-label={t('toggleMainMenu') || ''}
            variant="ghost"
            icon={IconMenu}
            onClick={onToggle}
          />
        </Box>
      </StyledAppBar>

      {isWorkspaceLevelRoute && (
        <CurrentWorkspaceRestriction permission="read_workspace_keys">
          <ApiHelper open={showHelper} onClose={showHelperHandler.hide}>
            {showHelper ? <WorkspaceKeyActionsDialog /> : null}
          </ApiHelper>
          {!showHelper ? <WorkspaceKeyActionsDialog /> : null}
        </CurrentWorkspaceRestriction>
      )}
    </>
  );
};
